import InputDateFields from '@components/InputDateFields'
import { BackIcon } from '@icons'
import {
  extractCheckoutMethodFieldOptionLabel,
  isCheckoutMethodFieldReadOnly,
  prepareCheckoutMethodFieldOptions,
  selectCurrencyOptions,
  useAppSelector,
  useBanksRequest
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, SupportedBank, UseAdditionalFieldsOutput } from 'mmfintech-checkout-commons/types'
import {
  fixDateOnly,
  formatCardNumber,
  formatCardValidityPartial,
  getCountryAlpha2,
  isEmptyString,
  isValidArray,
  parseCardValidity,
  tr
} from 'mmfintech-commons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { Fragment, useEffect } from 'react'
import { Link } from 'react-router-dom'

type AdditionalFieldsStepProps = {
  error?: unknown
  backVisible: boolean
  onBack: () => void
  countryCode?: string
  paymentMethod: string
  fieldValues: UseAdditionalFieldsOutput
  disabled: boolean
  submitCaption: string
  onSubmit: () => void
  cancelVisible: boolean
  onCancel: () => void
}

export const AdditionalFieldsStep = (props: AdditionalFieldsStepProps) => {
  const {
    error,
    backVisible,
    onBack,
    countryCode,
    paymentMethod,
    fieldValues,
    disabled,
    submitCaption,
    onSubmit,
    cancelVisible,
    onCancel
  } = props

  const currencies = useAppSelector(selectCurrencyOptions)

  const { banks } = useBanksRequest()

  // useEffect(() => {
  //     if (!(Array.isArray(fields) && fields.length > 0)) {
  //       typeof onSubmit === 'function' && onSubmit()
  //     }
  // }, [fields])

  // const getConditionValue = useCallback(
  //   (field: CheckoutMethodField): string | null => {
  //     if (isCheckoutMethodFieldConditionSetupValid(field)) {
  //       return fieldValues.getValue(field.condition.name)
  //     }
  //     return null
  //   },
  //   [fieldValues.getValue]
  // )

  // const isFieldHidden = useCallback(
  //   (field: CheckoutMethodField): boolean => isCheckoutMethodFieldHidden(field, getConditionValue(field)),
  //   [getConditionValue]
  // )

  // const isFieldRequired = useCallback(
  //   (field: CheckoutMethodField): boolean => isCheckoutMethodFieldRequired(field, getConditionValue(field)),
  //   [getConditionValue]
  // )

  // const extractOptionLabel = (field: CheckoutMethodField, value: string): string | null => {
  //   const options = prepareCheckoutMethodFieldOptions(field)
  //   return options.find(opt => opt.value === value)?.label || null
  // }

  // const handleFieldModified = useCallback(
  //   (name: string, value: string) => {
  //     const dependants = fields.filter(
  //       entry => isCheckoutMethodFieldConditionSetupValid(entry) && entry?.condition?.name === name
  //     )
  //     if (isValidArray(dependants)) {
  //       dependants.forEach((field: CheckoutMethodField) => {
  //         formValues.setRequired(field.name, isCheckoutMethodFieldRequired(field, value))
  //       })
  //     }
  //   },
  //   [fields]
  // )

  // biome-ignore lint/correctness/useExhaustiveDependencies: handle errors by fields
  useEffect(() => {
    fieldValues.handleErrors(error, {
      'cardDetails.cardNumber': 'cardNumber',
      'cardDetails.expiryMonth': 'expiryMonth',
      'cardDetails.expiryYear': 'expiryYear',
      'cardDetails.cvv': 'cvv'
    })
  }, [error])

  return (
    <div>
      {backVisible ? (
        <div className='back-button-wrapper'>
          <span onClick={onBack} data-test='back-button'>
            <BackIcon />
            <span>{tr('CHECKOUT.PAYMENT.SWITCH_METHOD', 'Switch method')}</span>
          </span>
        </div>
      ) : null}

      {paymentMethod ? (
        <div className='payment-method-wrapper' data-test={`pay-with-${paymentMethod}-fields`}>
          {tr('CHECKOUT.PAYMENT.PAY_BY', 'Pay with {{METHOD}}', {
            METHOD: tr(`METADATA.PAYMENT_OPTIONS.${paymentMethod}`, paymentMethod)
          })}
        </div>
      ) : null}

      <ErrorDisplay error={error} />

      {isValidArray(fieldValues.fields)
        ? fieldValues.fields.map((field: CheckoutMethodField) => {
            if (
              fieldValues.isFieldHidden(field) ||
              field.name === 'bankChoiceId' ||
              field.name === 'expiryYear' ||
              field.name === 'cvv'
            ) {
              return null
            }

            if (field.name === 'cardNumber') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type='text'
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    placeholder='0000 0000 0000 0000'
                    name={field.name}
                    value={formatCardNumber(fieldValues.getValue('cardNumber'))}
                    onChange={(_: string, value: string) =>
                      fieldValues.setValue('cardNumber', value.replace(/\D+/g, ''))
                    }
                    error={fieldValues.getError(field.name)}
                    maxLength={19}
                    required
                    hideErrorLine
                    hideShowPassword
                    autoComplete='off'
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.name === 'expiryMonth') {
              return (
                <div className='double-trouble' key={field.name}>
                  <Input
                    type='text'
                    name='expiryDate'
                    data-test={`expiryDate-input`}
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE', 'Expiry date')}
                    placeholder={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.EXPIRY_DATE_PLACEHOLDER', 'MM/YY')}
                    value={formatCardValidityPartial([
                      fieldValues.getValue('expiryMonth'),
                      fieldValues.getValue('expiryYear')
                    ])}
                    onChange={(_: string, value: string) => {
                      const [month, year] = parseCardValidity(value)
                      fieldValues.setValue('expiryMonth', month)
                      fieldValues.setValue('expiryYear', year)
                    }}
                    error={fieldValues.getError('expiryMonth') || fieldValues.getError('expiryYear')}
                    maxLength={5}
                    required
                    hideErrorLine
                    hideShowPassword
                    autoComplete='off'
                  />
                  <Input
                    type='password'
                    data-test={`cvv-input`}
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.CVV', 'CVV')}
                    {...fieldValues.registerInput('cvv')}
                    maxLength={4}
                    hideErrorLine
                    hideShowPassword
                    autoComplete='off'
                  />
                </div>
              )
            }

            if (field.name === 'swiftCode' && paymentMethod === 'INTERAC') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type='select'
                    data-test='interac-banks-select'
                    options={banks?.map((bank: SupportedBank) => {
                      const { bankId, name, nameEnglish } = bank
                      return { value: bankId, label: nameEnglish || name }
                    })}
                    label={tr('MERCHANTS.CHECKOUT.FIELDS.GENERAL.BANK', 'Bank')}
                    {...fieldValues.registerInput(field.name)}
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.name === 'phone') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type='phone'
                    data-test={`${field.name}-input`}
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    disabled={isCheckoutMethodFieldReadOnly(field)}
                    hideErrorLine
                    autoComplete='off'
                    {...(paymentMethod === 'INTERAC'
                      ? {
                          country: 'ca',
                          onlyCountries: ['ca'],
                          countryCodeEditable: false,
                          disableDropdown: true
                        }
                      : {
                          country: getCountryAlpha2(countryCode)?.toLowerCase()
                        })}
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.name === 'documentId' && paymentMethod === 'PIX') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type={field.type || 'text'}
                    label={tr(
                      'MERCHANTS.CHECKOUT.FIELDS.GENERAL.CPF_CNPJ',
                      field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel
                    )}
                    data-test='pix-document-id-input'
                    {...fieldValues.registerShort(field.name)}
                    onChange={(name: string, value: string) => fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))}
                    required={fieldValues.isFieldRequired(field)}
                    hideErrorLine
                    autoComplete='off'
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.name === 'currency') {
              return (
                <Fragment key={field.name}>
                  <Input
                    type='select'
                    data-test='currency-input'
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    {...fieldValues.registerInput(field.name)}
                    options={currencies?.map((currency: string) => ({ value: currency, label: currency }))}
                    hideErrorLine
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.type.toLowerCase() === 'date') {
              return (
                <Fragment key={field.name}>
                  <InputDateFields
                    label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                    value={fieldValues.getValue(field.name) ? new Date(fieldValues.getValue(field.name)) : null}
                    setValue={value => fieldValues.setValue(field.name, fixDateOnly(value))}
                    required={fieldValues.isFieldRequired(field)}
                    error={fieldValues.getError(field.name)}
                  />
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            if (field.type.toLowerCase() === 'select') {
              return (
                <Fragment key={field.name}>
                  {isCheckoutMethodFieldReadOnly(field) && !field.sensitive ? (
                    <Input
                      type='text'
                      data-test={`${field.name}-input`}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      value={extractCheckoutMethodFieldOptionLabel(field, fieldValues.getValue(field.name))}
                      readOnly={true}
                      hideErrorLine
                    />
                  ) : (
                    <Input
                      type='select'
                      data-test={`${field.name}-select`}
                      options={prepareCheckoutMethodFieldOptions(field)}
                      label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                      {...fieldValues.registerInput(field.name)}
                      hideErrorLine
                    />
                  )}
                  {Boolean(field.description) && (
                    <div className='input-description'>
                      {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                    </div>
                  )}
                </Fragment>
              )
            }

            return (
              <Fragment key={field.name}>
                <Input
                  type={field.sensitive ? 'password' : 'text'}
                  data-test={`${field.name}-input`}
                  label={field.translationKey ? tr(field.translationKey, field.defaultLabel) : field.defaultLabel}
                  {...fieldValues.registerInput(field.name)}
                  readOnly={isCheckoutMethodFieldReadOnly(field)}
                  hideErrorLine
                  hideShowPassword
                  autoComplete='off'
                />
                {Boolean(field.description) && (
                  <div className='input-description'>
                    {field.descriptionKey ? tr(field.descriptionKey, field.description) : field.description}
                  </div>
                )}
              </Fragment>
            )
          })
        : null}

      <div className='buttons'>
        <Button
          type='button'
          color='primary'
          text={submitCaption}
          onClick={onSubmit}
          disabled={isEmptyString(paymentMethod)}
          loading={disabled}
          data-test='button-pay'
        />

        {cancelVisible ? (
          <div className='cancel-button-container mt-1'>
            <Link to='#' onClick={onCancel} data-test='cancel-button'>
              {tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  )
}
